import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineFunnel, HiXMark } from "react-icons/hi2";

import { useVisibleCallsSearchQuery } from "../../../graphql";

const PAGE_LIMIT = 20;

type SelectRecordingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (
    callId: string,
    callName: string,
    positionTitle?: string | null
  ) => void;
};

function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export const SelectRecordingModal: React.FC<SelectRecordingModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const [filterQuery, setFilterQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  useEffect(() => {
    setSelectedCallId(null);
  }, [debouncedSearch]);

  const { data, loading, fetchMore, previousData } = useVisibleCallsSearchQuery(
    {
      variables: {
        search: debouncedSearch || null,
        pagination: { limit: PAGE_LIMIT },
      },
      fetchPolicy: "cache-and-network",
      skip: !isOpen,
    }
  );

  const displayData = data ?? previousData;
  const calls = displayData?.visibleCalls?.results ?? [];
  const pageInfo = displayData?.visibleCalls?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const fetchingMoreRef = useRef(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const hasNextPageRef = useRef(hasNextPage);
  const endCursorRef = useRef(pageInfo?.endCursor);
  hasNextPageRef.current = hasNextPage;
  endCursorRef.current = pageInfo?.endCursor;

  const handleScroll = useCallback(() => {
    const el = tableRef.current;
    if (!el || !hasNextPageRef.current || fetchingMoreRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      fetchingMoreRef.current = true;
      fetchMore({
        variables: {
          pagination: { limit: PAGE_LIMIT, after: endCursorRef.current },
        },
      }).finally(() => {
        fetchingMoreRef.current = false;
      });
    }
  }, [fetchMore]);

  const selectedCall = calls.find((c) => c.id === selectedCallId);

  const handleConfirm = () => {
    if (selectedCallId && selectedCall) {
      onSelect(
        selectedCallId,
        selectedCall.name ?? "recording",
        selectedCall.position?.displayTitle
      );
      setFilterQuery("");
      setDebouncedSearch("");
      setSelectedCallId(null);
    }
  };

  const handleClose = () => {
    setFilterQuery("");
    setDebouncedSearch("");
    setSelectedCallId(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent
        borderRadius="24px"
        mx="4"
        maxH="90vh"
        boxShadow="0px 20px 25px 0px rgba(0,0,0,0.1), 0px 10px 10px 0px rgba(0,0,0,0.04)"
      >
        {/* Header */}
        <Flex
          align="center"
          justify="space-between"
          gap="6"
          pt="6"
          pb="4"
          px="6"
        >
          <Text
            flex="1"
            fontSize="24px"
            fontWeight="semibold"
            lineHeight="32px"
            color="gray.800"
          >
            Select BrightHire recording
          </Text>
          <Flex
            as="button"
            align="center"
            justify="center"
            w="24px"
            h="24px"
            cursor="pointer"
            onClick={handleClose}
            flexShrink={0}
          >
            <Icon as={HiXMark} boxSize="5" color="gray.800" />
          </Flex>
        </Flex>

        {/* Content */}
        <ModalBody px="6" pt="6" pb="10">
          <Box p="4">
            {/* Filter input */}
            <InputGroup size="sm" mb="4">
              <InputLeftElement pointerEvents="none" h="40px" w="40px">
                <Icon as={HiOutlineFunnel} boxSize="5" color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Filter by keyword"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                h="40px"
                pl="40px"
                pr="3"
                fontSize="sm"
                lineHeight="20px"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="6px"
                color="gray.800"
                _placeholder={{ color: "gray.400" }}
                _focus={{ borderColor: "blue.400" }}
              />
            </InputGroup>

            {/* Table */}
            <Box
              ref={tableRef}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              minH="200px"
              maxH="480px"
              overflowX="hidden"
              overflowY="auto"
              onScroll={handleScroll}
            >
              {/* Table header */}
              <Flex
                bg="gray.50"
                h="36px"
                flexShrink={0}
                position="sticky"
                top="0"
                zIndex={1}
              >
                <Flex flex="1" minW="0" align="center" px="4" py="2">
                  <Text
                    fontSize="11.67px"
                    fontWeight="medium"
                    lineHeight="16px"
                    color="gray.500"
                    whiteSpace="nowrap"
                  >
                    Date
                  </Text>
                </Flex>
                <Flex flex="2" minW="0" align="center" px="4" py="2">
                  <Text
                    fontSize="11.67px"
                    fontWeight="medium"
                    lineHeight="16px"
                    color="gray.500"
                    whiteSpace="nowrap"
                  >
                    Position
                  </Text>
                </Flex>
                <Flex flex="1.5" minW="0" align="center" px="4" py="2">
                  <Text
                    fontSize="11.67px"
                    fontWeight="medium"
                    lineHeight="16px"
                    color="gray.500"
                    whiteSpace="nowrap"
                  >
                    Interviewer
                  </Text>
                </Flex>
                <Flex flex="1" align="center" px="4" py="2">
                  <Text
                    fontSize="11.67px"
                    fontWeight="medium"
                    lineHeight="16px"
                    color="gray.500"
                    whiteSpace="nowrap"
                  >
                    Recording
                  </Text>
                </Flex>
              </Flex>

              {/* Loading state — only on initial load, not during fetchMore */}
              {loading && calls.length === 0 && !fetchingMoreRef.current && (
                <Flex align="center" justify="center" py="10">
                  <Spinner size="sm" color="accent.600" />
                </Flex>
              )}

              {/* Empty state */}
              {!loading && calls.length === 0 && (
                <Flex align="center" justify="center" py="10">
                  <Text fontSize="sm" color="gray.400">
                    {filterQuery
                      ? "No recordings match your filter"
                      : "No recordings found"}
                  </Text>
                </Flex>
              )}

              {/* Table rows */}
              {calls.map((call) => {
                const isSelected = call.id === selectedCallId;
                return (
                  <Flex
                    key={call.id}
                    as="button"
                    w="100%"
                    h="48px"
                    textAlign="left"
                    bg={isSelected ? "accent.50" : "white"}
                    cursor="pointer"
                    transition="background 0.1s"
                    _hover={!isSelected ? { bg: "gray.50" } : {}}
                    onClick={() => setSelectedCallId(call.id)}
                  >
                    <Flex flex="1" minW="0" align="center" px="4" py="3">
                      <Text
                        fontSize="sm"
                        fontWeight="normal"
                        lineHeight="20px"
                        color="gray.800"
                        noOfLines={1}
                      >
                        {formatDate(call.startTime)}
                      </Text>
                    </Flex>
                    <Flex flex="2" minW="0" align="center" px="4" py="3">
                      <Text
                        fontSize="sm"
                        fontWeight="normal"
                        lineHeight="20px"
                        color="gray.800"
                        noOfLines={1}
                      >
                        {call.position?.displayTitle?.trim() ?? "—"}
                      </Text>
                    </Flex>
                    <Flex flex="1.5" minW="0" align="center" px="4" py="3">
                      <Text
                        fontSize="sm"
                        fontWeight="normal"
                        lineHeight="20px"
                        color="gray.800"
                        noOfLines={1}
                      >
                        {call.interviewers?.length
                          ? call.interviewers.map((i) => i.fullName).join(", ")
                          : "—"}
                      </Text>
                    </Flex>
                    <Flex flex="1" align="center" px="4" py="3">
                      <Text
                        fontSize="sm"
                        fontWeight="normal"
                        lineHeight="20px"
                        color="gray.800"
                        noOfLines={1}
                      >
                        {call.name ?? "—"}
                      </Text>
                    </Flex>
                  </Flex>
                );
              })}

              {/* Infinite scroll loading indicator */}
              {hasNextPage && loading && calls.length > 0 && (
                <Flex align="center" justify="center" py="3">
                  <Spinner size="sm" color="accent.600" />
                </Flex>
              )}
            </Box>
          </Box>
        </ModalBody>

        {/* Footer */}
        <Flex
          align="center"
          justify="space-between"
          p="6"
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <Flex
            as="button"
            align="center"
            h="40px"
            px="4"
            borderRadius="8px"
            color="accent.600"
            fontSize="16.8px"
            fontWeight="medium"
            lineHeight="24px"
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ bg: "gray.50" }}
            onClick={handleClose}
          >
            Cancel
          </Flex>
          <Flex
            as="button"
            align="center"
            justify="center"
            gap="2"
            h="40px"
            px="4"
            bg="accent.600"
            border="1px solid"
            borderColor="purple.450"
            borderRadius="8px"
            color="white"
            fontSize="16.8px"
            fontWeight="medium"
            lineHeight="24px"
            cursor={selectedCallId ? "pointer" : "not-allowed"}
            opacity={selectedCallId ? 1 : 0.5}
            transition="all 0.15s"
            _hover={selectedCallId ? { opacity: 0.9 } : {}}
            _active={selectedCallId ? { opacity: 0.8 } : {}}
            onClick={selectedCallId ? handleConfirm : undefined}
          >
            Add recording transcript
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
};
